import {WriteButton} from '../../../common/buttons/WriteActions';
import {mobileDialogSx} from '../../../common/dialogs/mobileDialogSx';
import {LocalizedDateField} from '../../../common/dates/LocalizedDateField';
import {getCurrencySymbol} from '../../../../common/currency';
import {useEffect, useState} from 'react';
import {Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, InputAdornment, Stack, TextField, Typography, useMediaQuery, useTheme} from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import {PackageGuidDto, PackageStockCreateRequest} from '../../../../models/Packages/IPackage';
import {UNITS} from '@mybake/ui/common/dictionary';
import {packageAPI} from '../../../../services/PackageServices';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {useCurrency} from '../../../../hooks/useCurrency';
import {getApiErrorMessage, getApiFieldErrors} from '../../../../common/apiErrors';
import {LocalizedDecimalField} from '../../../common/inputs/LocalizedDecimalField';

type Props = { pack: PackageGuidDto; open: boolean; onClose: () => void; onSaved?: () => void; };

const localToday = () => {
    const date = new Date();
    return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
};

export const AddPackagePurchaseDialog = (props: Props) => props.open ? <PackagePurchaseDialogContent key={props.pack.id} {...props}/> : null;

const PackagePurchaseDialogContent = ({pack, open, onClose, onSaved}: Props) => {
    const {currency} = useCurrency();
    const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
    const {t} = useAppTranslation();
    const [updatePackage, {isLoading, error}] = packageAPI.useUpdatePackageMutation();
    const [isMultipack, setIsMultipack] = useState(false);
    const [stock, setStock] = useState<PackageStockCreateRequest>({
        quantity: 0, price: 0, unitValue: pack.unit === UNITS.PCS ? 1 : null, purchaseDate: localToday(),
    });

    useEffect(() => {
        if (open) {
            setIsMultipack(false);
            setStock({
                quantity: 0, price: 0,
                unitValue: pack.unit === UNITS.PCS ? 1 : null, purchaseDate: localToday(),
            });
        }
    }, [open, pack.id, pack.unit]);

    const setNumber = (field: 'quantity' | 'price') => (event: React.ChangeEvent<HTMLInputElement>) =>
        setStock(previous => ({...previous, [field]: Number(event.target.value)}));
    const setUnitValue = (event: React.ChangeEvent<HTMLInputElement>) =>
        setStock(previous => ({...previous, unitValue: event.target.value === '' ? null : Number(event.target.value)}));
    const isValid = stock.quantity > 0 && stock.price > 0 && !!stock.purchaseDate &&
        (pack.unit === UNITS.M
            ? (stock.unitValue ?? 0) > 0
            : !isMultipack || (stock.unitValue ?? 0) >= 1);

    const submit = async () => {
        if (!isValid) return;
        if (!pack.id) return;
        await updatePackage({id: pack.id, body: {id: pack.id, name: pack.name, unit: pack.unit, stocks: [stock], productTypes: pack.productTypes}}).unwrap();
        onSaved?.();
        onClose();
    };

    return (
        <Dialog sx={mobileDialogSx} open={open} onClose={isLoading ? undefined : onClose} fullWidth maxWidth="xs" PaperProps={{sx: {borderRadius: 3}}}>
            <DialogTitle>{t('items:add_purchase')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 1}}>
                    <Typography color="text.secondary" variant="body2">{pack.name} · {pack.unit}</Typography>
                    {error && <Alert severity="error">{getApiErrorMessage(error, t)}</Alert>}
                    <TextField autoFocus required fullWidth label={t('items:purchase_quantity')} type="number"
                               inputProps={{min: 0, step: 'any'}} value={stock.quantity || ''} onChange={setNumber('quantity')} error={!!getApiFieldErrors(error, t).quantity} helperText={getApiFieldErrors(error, t).quantity}/>
                    <LocalizedDecimalField required fullWidth label={t('items:purchase_price')}
                               inputProps={{min: 0, step: '0.01'}} InputProps={{endAdornment: <InputAdornment position="end">{getCurrencySymbol(currency)}</InputAdornment>}}
                               value={stock.price || undefined} onValueChange={price => setStock(previous => ({...previous, price: price ?? 0}))}/>
                    {pack.unit === UNITS.PCS && <FormControlLabel
                        control={<Checkbox checked={isMultipack} onChange={event => {
                            setIsMultipack(event.target.checked);
                            if (!event.target.checked) setStock(previous => ({...previous, unitValue: 1}));
                        }}/>} label={t('items:is_multipack')}/>} 
                    {(pack.unit === UNITS.M || isMultipack) && <TextField required fullWidth
                        label={t(pack.unit === UNITS.M ? 'items:unit_value_meters' : 'items:unit_value_pcs')}
                        helperText={t(pack.unit === UNITS.M ? 'items:unit_value_meters_help' : 'items:unit_value_pcs_help')}
                        type="number" inputProps={{min: pack.unit === UNITS.M ? 0 : 1, step: 'any'}} value={stock.unitValue ?? ''}
                        onChange={setUnitValue}/>} 
                    <LocalizedDateField required fullWidth label={t('items:purchase_date')} type="date" value={stock.purchaseDate ?? ''}
                               onChange={(event) => setStock(previous => ({...previous, purchaseDate: event.target.value}))}
                               InputLabelProps={{shrink: true}}/>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2, gap: 1}}>
                <Button onClick={onClose} disabled={isLoading}>{t('common:cancel')}</Button>
                <Box sx={{display: {xs: 'none', sm: 'block'}, flex: 1}}/>
                <WriteButton variant="contained" onClick={submit} disabled={!isValid || isLoading} sx={{'& .MuiButton-startIcon': {display: {xs: 'none', sm: 'inherit'}}}} startIcon={<AddShoppingCartIcon/>}>{t(isMobile ? 'common:save' : 'items:save_purchase')}</WriteButton>
            </DialogActions>
        </Dialog>
    );
};
